import { useQuery } from "@tanstack/react-query";
import { liveProgressApi } from "../api/liveProgress";
import { useLiveProgressProjectsMutation } from "../mutations";

export interface Project {
  id: number;
  title?: string;
  status?: string;
  project_status?: string;
  total_budget?: number;
  total_spent?: number | null;
  total_remaining?: number | null;
  planned_start_date?: string;
  planned_end_date?: string;
  progress?: { percentage?: number };
  project_scope?: string;
  contractor?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  rag?: {
    time?: string;
    cost?: string;
    quality?: string;
    overall?: string;
  };
  project_reference?: string;
  programme?: string;
  network?: string;
  workstream?: string;
  site?: string;
  phase_template?: string;
  phases?: Array<{
    name?: string;
    status?: string;
    percentage_complete?: number;
  }>;
}

const generateRandomValue = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

const mapApiProjectToProject = (apiProject: {
  id: number;
  title: string;
  project_reference: string;
  description: string;
  project_scope: string;
  project_budget: number;
  actual_spend: number | null;
  planned_start_date: string;
  planned_end_date: string | null;
  planned_commissioning_date: string;
  planned_close_up_date: string;
  status: string;
  project_status: string;
  address: string;
  coordinates: {
    type: string;
    coordinates: [number, number];
  };
  rag: {
    time: string;
    cost: string;
    quality: string;
    overall: string;
  };
  progress: {
    total: number;
    percentage: number;
  };
  phases: Array<{
    phase_id: number;
    name: string;
    project_id: number;
    display_order: number;
    status: string;
    percentage_complete: number;
  }>;
  programme: string;
  network: string;
  workstream: string;
  site: string;
  phase_template: string;
}): Project => {
  const budget = apiProject.project_budget || generateRandomValue(50000, 500000);
  const spent = apiProject.actual_spend ?? generateRandomValue(budget * 0.1, budget * 0.8);
  const remaining = budget - spent;

  return {
    id: apiProject.id,
    title: apiProject.title,
    status: apiProject.status,
    project_status: apiProject.project_status,
    total_budget: budget,
    total_spent: spent,
    total_remaining: remaining,
    planned_start_date: apiProject.planned_start_date,
    planned_end_date: apiProject.planned_end_date || apiProject.planned_close_up_date,
    progress: {
      percentage: apiProject.progress?.percentage || 0,
    },
    project_scope: apiProject.project_scope,
    contractor: undefined,
    address: apiProject.address,
    latitude: apiProject.coordinates?.coordinates?.[1],
    longitude: apiProject.coordinates?.coordinates?.[0],
    rag: apiProject.rag,
    project_reference: apiProject.project_reference,
    programme: apiProject.programme,
    network: apiProject.network,
    workstream: apiProject.workstream,
    site: apiProject.site,
    phase_template: apiProject.phase_template,
    phases: apiProject.phases?.map((phase) => ({
      name: phase.name,
      status: phase.status,
      percentage_complete: phase.percentage_complete,
    })),
  };
};

export const useLiveProgressProjects = (params?: {
  page?: number;
  limit?: number;
}) => {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["live-progress-projects", params],
    queryFn: () => liveProgressApi.fetchProjects(params),
  });

  const mutation = useLiveProgressProjectsMutation();

  const projects: Project[] = data?.data?.rows
    ? data.data.rows.map(mapApiProjectToProject)
    : [];

  return {
    projects,
    pagination: data?.data?.pagination,
    isLoading: isLoading || mutation.isPending,
    isError,
    error:
      error instanceof Error
        ? error.message
        : "Failed to fetch projects",
    refetch: () => mutation.mutate(params),
  };
};

